/* ==========================================================================
   SlugInput Component
   Task F34: Portal URL slug input with debounced availability check
   ========================================================================== */

import { useState, useEffect, useRef, useId } from 'react'
import { Check, X, AlertCircle } from 'lucide-react'
import { Spinner } from '@/components/ui/Spinner'
import { checkSlugAvailability, type SlugAvailability } from '@/api/intake'

interface SlugInputProps {
  token: string
  value: string
  onChange: (slug: string) => void
  error?: string
}

export function SlugInput({ token, value, onChange, error }: SlugInputProps) {
  const id = useId()
  const [checking, setChecking] = useState(false)
  const [availability, setAvailability] = useState<SlugAvailability | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasError = Boolean(error)

  // Auto-lowercase and strip invalid chars
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '')
      .replace(/--+/g, '-')
    onChange(raw)
    setAvailability(null)
  }

  // Debounced availability check
  useEffect(() => {
    if (debounceRef.current !== null) clearTimeout(debounceRef.current)

    // Must be at least 2 chars and valid format
    const isValid = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(value) && !value.includes('--')
    if (!isValid || value.length < 2) {
      setAvailability(null)
      return
    }

    setChecking(true)
    debounceRef.current = setTimeout(async () => {
      try {
        const result = await checkSlugAvailability(token, value)
        setAvailability(result)
      } catch {
        setAvailability(null)
      } finally {
        setChecking(false)
      }
    }, 300)

    return () => {
      if (debounceRef.current !== null) clearTimeout(debounceRef.current)
    }
  }, [value, token])

  // Visual indicator
  const renderStatus = () => {
    if (checking) {
      return <Spinner size="xs" label="Checking availability" />
    }
    if (availability?.available === true) {
      return <Check className="h-4 w-4 text-green-600" aria-label="Available" />
    }
    if (availability?.available === false) {
      return <X className="h-4 w-4 text-error" aria-label="Taken" />
    }
    return null
  }

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-gray-700">
        Portal URL
        <span className="text-error ml-0.5" aria-hidden="true">*</span>
      </label>

      <div className="flex items-center">
        {/* URL prefix */}
        <span className="inline-flex items-center px-3 py-3 text-sm text-gray-500 bg-gray-50 border border-r-0 border-gray-300 rounded-l-md whitespace-nowrap">
          portal.tjmlabs.com/rx/
        </span>
        {/* Slug input */}
        <div className="relative flex-1">
          <input
            id={id}
            type="text"
            value={value}
            onChange={handleChange}
            placeholder="your-pharmacy"
            maxLength={50}
            className={`
              w-full px-3 py-3 text-base
              border rounded-r-md bg-white
              text-charcoal placeholder:text-gray-400
              transition-[border-color,box-shadow] duration-150
              focus:outline-none
              ${hasError || availability?.available === false
                ? 'border-error focus:border-error focus:shadow-[0_0_0_3px_rgba(198,40,40,0.2)]'
                : 'border-gray-300 focus:border-deep-purple focus:shadow-[0_0_0_3px_rgba(74,53,135,0.15)]'
              }
            `}
            aria-invalid={hasError || undefined}
          />
          {/* Status indicator */}
          <span className="absolute inset-y-0 right-0 flex items-center pr-3">
            {renderStatus()}
          </span>
        </div>
      </div>

      {/* Error or availability messages */}
      {error && (
        <p className="text-sm text-error flex items-center gap-1" role="alert">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          {error}
        </p>
      )}
      {!error && availability?.available === false && availability.suggestion && (
        <p className="text-sm text-gray-600">
          This slug is taken. Try{' '}
          <button
            type="button"
            onClick={() => onChange(availability.suggestion!)}
            className="text-deep-purple font-medium hover:underline"
          >
            {availability.suggestion}
          </button>
          ?
        </p>
      )}
      {!error && availability?.available === true && (
        <p className="text-sm text-green-600">This slug is available!</p>
      )}
    </div>
  )
}

export default SlugInput
